import { cn } from '@/lib';
import {
  useEffect,
} from 'react';
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';
import { SelectDropdown } from './SelectDropdown';

const inputClasses =
  'block w-full py-2 px-3 text-sm rounded-lg border bg-surface text-onsurface placeholder:text-muted-foreground focus:outline-none focus:ring-1 border-border focus:ring-primary focus:border-primary';
const readOnlyValueClasses =
  'block w-full px-0 text-sm font-medium bg-surface text-onsurface';

const labelClasses = 'block mb-1 text-sm font-medium text-onsurface';
const clickOnlyButtonClasses =
  'border-border bg-field text-onfield hover:bg-surface-darker/60 flex w-full items-center justify-between rounded-sm border px-3 py-2 text-sm transition-colors disabled:opacity-50';

export interface FormFieldLabelProps {
  label: string;
  labelKey?: string;
  htmlFor?: string;
  noValue?: boolean;
  mandatory?: boolean;
}

export function FormFieldLabel({ label, labelKey, htmlFor, mandatory = false, noValue = false }: FormFieldLabelProps) {
  const { t } = useTranslation();
  const displayLabel = labelKey ? t(labelKey) : label;

  let opacityClass = '';

  if (noValue && mandatory) {
    opacityClass = 'font-bold opacity-100 ';
  } else {
    opacityClass = "opacity-60"
  }

  return (
    <label htmlFor={htmlFor} className={`${labelClasses} ${opacityClass}`}>
      {displayLabel}
    </label>
  );
}

export interface FormFieldAnyProps {
  label: string;
  labelKey?: string;
  error?: string;
  className?: string;
  mandatory?: boolean;
  htmlFor?: string;
  noValue?: boolean;
  children: ReactNode;
}

export function FormFieldAny({
  label,
  labelKey,
  error,
  className = '',
  mandatory = false,
  htmlFor,
  noValue = false,
  children,
}: FormFieldAnyProps) {
  return (
    <div className={className}>
      <FormFieldLabel label={label} labelKey={labelKey} htmlFor={htmlFor} mandatory={mandatory} noValue={noValue} />
      {children}
      {error && <p className="mt-1 text-sm text-error">{error}</p>}
    </div>
  );
}

export interface FormLabelProps {
  label: string;
  labelKey?: string;
  value?: string | null;
  error?: string;
  className?: string;
  mandatory?: boolean;
  emptyValue?: string;
}

/**
 * @component FormLabel
 * @description Renders a read-only value with the same label and spacing contract as other form fields.
 *
 * @props
 *   label (string, required) – fallback label text.
 *   labelKey (string, optional) – translation key for the field label.
 *   value (string | null, optional) – read-only value shown in the field body.
 *   error (string, optional) – validation or submit error shown below the value.
 *   className (string, optional, default: '') – layout classes for the field wrapper.
 *   mandatory (boolean, optional, default: false) – marks an empty required value in the label style.
 *   emptyValue (string, optional, default: '') – value shown when the field has no value.
 *
 * @changelog
 *   2026-07-08 – Initial creation for read-only customer ID display.
 */
export function FormLabel({
  label,
  labelKey,
  value,
  error,
  className = '',
  mandatory = false,
  emptyValue = '',
}: FormLabelProps) {
  const displayValue = value || emptyValue;

  return (
    <FormFieldAny
      label={label}
      labelKey={labelKey}
      error={error}
      className={className}
      mandatory={mandatory}
      noValue={!displayValue}
    >
      <p className={cn(readOnlyValueClasses, !displayValue && 'opacity-60')} aria-readonly="true">
        {displayValue}
      </p>
    </FormFieldAny>
  );
}

export interface FormFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label: string;
  labelKey?: string;
  error?: string;
  className?: string;
  mandatory?: boolean;
}

export function FormField({
  label,
  labelKey,
  error,
  className = '',
  id,
  mandatory = false,
  style,
  ...inputProps
}: FormFieldProps) {
  const fieldId = id || inputProps.name;

  return (
    <FormFieldAny
      label={label}
      labelKey={labelKey}
      error={error}
      className={className}
      mandatory={mandatory}
      htmlFor={fieldId}
      noValue={!inputProps.value}
    >
      <input id={fieldId} className={inputClasses} style={{
        ...style,
      }} {...inputProps} />
    </FormFieldAny>
  );
}

export interface FormSelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  label: string;
  labelKey?: string;
  error?: string;
  className?: string;
  children: ReactNode;
}

export function FormSelect({
  label,
  labelKey,
  error,
  className = '',
  id,
  children,
  ...selectProps
}: FormSelectProps) {
  const fieldId = id || selectProps.name;

  useEffect(() => {
    // Dynamically import to avoid SSR / hydration issues
    import("preline").then(({ HSSelect }) => {
      HSSelect.autoInit();
    });
  }, []);

  return (
    <FormFieldAny
      label={label}
      labelKey={labelKey}
      error={error}
      className={className}
      htmlFor={fieldId}
    >
      <SelectDropdown
        id={fieldId}
        {...selectProps}
      >
        {children}
      </SelectDropdown>
    </FormFieldAny>
  );
}

export interface FormClickOnlyProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> {
  label: string;
  labelKey?: string;
  loading?: boolean;
  value?: string;
  actionLabel: string;
  className?: string;
  mandatory?: boolean;
}

export function FormClickOnly({
  label,
  labelKey,
  value,
  actionLabel,
  className = '',
  loading = false,
  mandatory = false,
  style,
  ...buttonProps
}: FormClickOnlyProps) {
  const { t } = useTranslation();

  let internalValue = ""
  let opacityClass = "opacity-100"


  if (loading) {
    internalValue = t('common.loading')
    opacityClass = "opacity-50"
  } else if (value === undefined) {
    internalValue = t('common.notSet')
    opacityClass = "opacity-50"

  } else {
    internalValue = value
    opacityClass = "opacity-100"
  }

  return (
    <FormFieldAny
      label={label}
      labelKey={labelKey}
      className={className}
      mandatory={mandatory}
      noValue={value === undefined}
    >
      <button type="button" className={clickOnlyButtonClasses} style={{
        ...style
      }} {...buttonProps}>
        <span className={cn("truncate", opacityClass)}>{internalValue}</span>
        <span className="opacity-60">{actionLabel}</span>
      </button>
    </FormFieldAny>
  );
}
